import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Minus, Plus, Trash2, CreditCard, Banknote, X, User, Phone, 
  LogOut, Settings, UtensilsCrossed, ShoppingBag, Loader2, Gift, Search, 
  FileText, CheckCircle, Clock, XCircle, Send, Printer, AlertTriangle,
  Square, Moon, Calendar, Eye, Split, Cloud, Timer, RefreshCw, Package,
  Smartphone, Check, UserPlus, ChevronDown, Star, Store
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger
} from "@/components/ui/drawer";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { useAuthStore, useCartStore, useThemeStore, api } from "@/store/useStore";
import { formatCurrency, cn, formatDate, resolveImageUrl } from "@/lib/utils";
import { isIminDevice } from "@/services/iminPrinter";
import SplitBillDialog from "@/components/SplitBillDialog";
import ProductCustomizationModal from "@/components/ProductCustomizationModal";
import { usePOSPrint } from "@/hooks/usePOSPrint";

// POS Components (Extracted dialogs - Feb 2026)
import { VoidItemDialog, ShiftDialog, ViewInvoiceDialog, CheckoutDialog, C2BInstructionsDialog, DpoTerminalDialog, PesapalTerminalDialog } from "@/components/pos";
import { generateCardSlipHTML, openPrintWindow } from "@/lib/print-utils";
import { POSCustomerSheet } from "@/components/pos/POSCustomerSheet";

// PWA imports
import { NetworkIndicator, SyncStatusPanel } from "@/components/pwa/NetworkStatus";
import { PrinterIndicator, PrinterManagerDialog } from "@/components/pwa/PrinterManager";
import { useNetworkStatus, usePOSCache, useSyncStatus } from "@/lib/pwa-hooks";
import offlineDB from "@/lib/offline-storage";
import { createOfflineOrder, getPendingOfflineOrders, syncAllOfflineOrders } from "@/lib/offline-orders";

// Security
import { IdleTimeoutSettingsInline } from "@/components/IdleTimeoutSettings";
import { updateLastActivity } from "@/lib/idle-timeout";
import { useQROrderNotifications } from "@/hooks/useQROrderNotifications";

export default function POSPage() {
  const navigate = useNavigate();
  const { user, currentOutlet, logout, setCurrentOutlet } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const {
    items, addItem, removeItem, updateQuantity, getSubtotal, getTax, getTotal,
    clearCart, customerName, customerPhone, setCustomerInfo, orderType, setOrderType,
    setTaxMethod, setTaxRate, taxCalculationMethod
  } = useCartStore();

  // PWA Hooks
  const { isOnline, triggerSync } = useNetworkStatus();
  const { cacheProducts, cacheCategories, getCachedProducts, getCachedCategories } = usePOSCache();
  const { syncStatus } = useSyncStatus();
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [showPrinterManager, setShowPrinterManager] = useState(false);
  const [pendingOfflineOrders, setPendingOfflineOrders] = useState([]);

  // QR Order live notifications
  const { pendingCount: qrPendingCount, pendingOrders: qrPendingOrders, acknowledge: ackQrOrders, refresh: refreshQrOrders } = useQROrderNotifications({ enabled: true, sound: true, alertRepeat: 3 });

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [outletSwitching, setOutletSwitching] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [amountTendered, setAmountTendered] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [mpesaPhone, setMpesaPhone] = useState("");
  const [mpesaMethod, setMpesaMethod] = useState("stk_push"); // stk_push, c2b, or c2b_verify
  const [mpesaSettings, setMpesaSettings] = useState(null); // Property M-Pesa settings
  const [processing, setProcessing] = useState(false);
  const etimsPreflightRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerSheetOpen, setCustomerSheetOpen] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  
  // C2B Verification State
  const [c2bMpesaCode, setC2bMpesaCode] = useState("");
  const [c2bVerifyResult, setC2bVerifyResult] = useState(null);
  const [c2bVerifying, setC2bVerifying] = useState(false);
  const [availableC2BPayments, setAvailableC2BPayments] = useState([]);
  const [loadingC2BPayments, setLoadingC2BPayments] = useState(false);
  const [selectedC2BPayment, setSelectedC2BPayment] = useState(null);
  
  // Outlet Settings
  const [outletSettings, setOutletSettings] = useState({ service_mode: "invoice" });
  
  // Invoice Template Settings
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  
  // Invoice Management
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [closedInvoices, setClosedInvoices] = useState([]);
  const [invoiceTab, setInvoiceTab] = useState("open");
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidItem, setVoidItem] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [viewInvoiceOpen, setViewInvoiceOpen] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  
  // C2B Payment Dialog
  const [c2bDialogOpen, setC2bDialogOpen] = useState(false);
  const [c2bInvoice, setC2bInvoice] = useState(null);
  
  // Card Payment State
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [cardSettings, setCardSettings] = useState(null); // Property Cybersource settings
  const [dpoSettings, setDpoSettings] = useState(null); // Property DPO card-terminal settings
  const [showDpoDialog, setShowDpoDialog] = useState(false); // DPO push-to-pay status modal
  const [dpoInvoice, setDpoInvoice] = useState(null); // Invoice being settled via DPO terminal
  const [pesapalPosSettings, setPesapalPosSettings] = useState(null); // Property Pesapal card-terminal settings
  const [showPesapalPosDialog, setShowPesapalPosDialog] = useState(false); // Pesapal push-to-pay status modal
  const [pesapalPosInvoice, setPesapalPosInvoice] = useState(null); // Invoice being settled via Pesapal terminal
  const [cardDetails, setCardDetails] = useState({
    card_number: "",
    expiry_month: "",
    expiry_year: "",
    cvv: "",
    cardholder_name: ""
  });
  
  // NFC Payment State
  const [nfcDialogOpen, setNfcDialogOpen] = useState(false);
  const [nfcPaymentStatus, setNfcPaymentStatus] = useState("idle"); // idle, pending, waiting, processing, success, failed
  const [nfcPaymentRequestId, setNfcPaymentRequestId] = useState(null);
  const [nfcPollingInterval, setNfcPollingInterval] = useState(null);
  
  // Product Customization State
  const [customizationModalOpen, setCustomizationModalOpen] = useState(false);
  const [productToCustomize, setProductToCustomize] = useState(null);
  
  // Credit Sale State
  const [creditCheckResult, setCreditCheckResult] = useState(null);
  const [creditApprovalPending, setCreditApprovalPending] = useState(false);
  const [pendingCreditApproval, setPendingCreditApproval] = useState(null);
  
  // Split Bill Dialog
  const [splitBillOpen, setSplitBillOpen] = useState(false);
  
  // Shift Management (for supervisors)
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const [businessDate, setBusinessDate] = useState(null);
  const [closingCash, setClosingCash] = useState("");
  const [bankDeposit, setBankDeposit] = useState("");
  const [floatRetained, setFloatRetained] = useState("");
  const [shiftNotes, setShiftNotes] = useState("");
  const [shiftLoading, setShiftLoading] = useState(false);
  
  // Printer & Device Management
  const [currentDevice, setCurrentDevice] = useState(null); // The POS device this is running on
  const [printDestinations, setPrintDestinations] = useState([]); // Configured print destinations for outlet
  const [isImin, setIsImin] = useState(false); // Whether running on iMin device
  
  // Print hook - extracted for maintainability
  const {
    handleAutoPrint,
    handlePrintInvoice,
    printShiftSummary,
    printOrderReceipt: _printOrderReceiptBase,
    printOfflineReceipt,
    printReceiptBrowser,
    generatePrintHTMLLocal,
  } = usePOSPrint({
    currentDevice,
    printDestinations,
    isImin,
    invoiceTemplate,
    currentOutlet,
    user,
  });

  // Wrap printOrderReceipt to inject local state
  const printOrderReceipt = (order, orderData) =>
    _printOrderReceiptBase(order, orderData, {
      selectedTable, items, getSubtotal, getTax, getTotal, paymentMethod, promoDiscount, loyaltyDiscount,
    });

  // User permissions - check for various permission formats
  const userPermissions = user?.role?.permissions || [];
  const hasPermission = (perm) => {
    if (userPermissions.includes("all") || userPermissions.includes("*")) return true;
    if (userPermissions.includes(perm)) return true;
    // Check category wildcard (e.g., "shift.*")
    const category = perm.split(".")[0];
    if (userPermissions.includes(`${category}.*`)) return true;
    return false;
  };
  
  // Special check for restriction permissions - these should only be true if EXPLICITLY set
  const hasRestriction = (perm) => {
    return userPermissions.includes(perm); // Only exact match, no wildcards
  };
  
  // POS Permissions
  const canAccessPOS = hasPermission("pos.access") || userPermissions.length === 0; // Default allow if no permissions set
  const canCreateInvoice = hasPermission("pos.create_invoice") || userPermissions.length === 0;
  const canSettleInvoice = hasPermission("pos.settle_invoice") || userPermissions.length === 0;
  const canSplitInvoice = hasPermission("pos.split_invoice") || userPermissions.length === 0;
  const canVoidItems = hasPermission("pos.void_order") || hasPermission("void_items");
  const canApplyDiscount = hasPermission("pos.apply_discount");
  const canProcessRefund = hasPermission("pos.refund");
  const canCreditSale = hasPermission("pos.credit_sale") || hasPermission("pos.*");
  const noCashPayments = hasRestriction("pos.no_cash"); // Restriction - only explicit, no wildcards
  
  // Shift Permissions
  const canCloseShift = hasPermission("shift.close") || hasPermission("shift.close_any") || hasPermission("close_shift");
  const canRunEod = hasPermission("shift.end_day") || hasPermission("eod");

  // Set default payment method to mpesa if cash is restricted
  useEffect(() => {
    if (noCashPayments && paymentMethod === "cash") {
      setPaymentMethod("mpesa");
    }
  }, [noCashPayments, paymentMethod]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      // ===== PHASE 1: Instant load from IndexedDB cache =====
      const hasCachedData = await loadFromCache();
      if (hasCachedData) {
        setLoading(false); // POS is usable immediately
      }

      // ===== PHASE 2: Refresh from network in background =====
      if (isOnline) {
        await refreshFromNetwork(!hasCachedData);
      } else if (!hasCachedData) {
        toast.error("No cached data available. Please connect to the internet.");
      }
    } catch (error) {
      if (products.length === 0) {
        // Last resort: try cache on any error
        await loadFromCache();
      }
    } finally {
      setLoading(false);
    }
  };

  /** Load cached POS data from IndexedDB for instant display */
  const loadFromCache = async () => {
    try {
      const [cachedProducts, cachedCategories] = await Promise.all([
        getCachedProducts(),
        getCachedCategories()
      ]);
      
      if (cachedProducts.length > 0 && cachedCategories.length > 0) {
        setProducts(cachedProducts);
        setCategories(cachedCategories);
        
        // Also restore outlet settings, template, tables from cache
        const outletId = currentOutlet?.id;
        if (outletId) {
          const [cachedOutlet, cachedTemplate, cachedTables, cachedDestinations, cachedPropertySettings] = await Promise.all([
            offlineDB.getCachedData(`outlet_${outletId}`),
            offlineDB.getCachedData(`template_${outletId}`),
            offlineDB.getCachedData('tables'),
            offlineDB.getCachedData(`destinations_${outletId}`),
            offlineDB.getCachedData('property_settings')
          ]);
          if (cachedOutlet) setOutletSettings(cachedOutlet);
          if (cachedTemplate) setInvoiceTemplate(cachedTemplate);
          if (cachedTables) setTables(cachedTables.filter(t => t.status === "available"));
          if (cachedDestinations) setPrintDestinations(cachedDestinations);
          if (cachedPropertySettings) {
            if (cachedPropertySettings.taxMethod) setTaxMethod(cachedPropertySettings.taxMethod);
            if (cachedPropertySettings.taxRate) setTaxRate(cachedPropertySettings.taxRate);
            if (cachedPropertySettings.mpesaSettings) {
              setMpesaSettings(cachedPropertySettings.mpesaSettings);
              setMpesaMethod(cachedPropertySettings.mpesaSettings.default_payment_method || 'stk_push');
            }
            if (cachedPropertySettings.cardSettings) setCardSettings(cachedPropertySettings.cardSettings);
            if (cachedPropertySettings.dpoSettings) setDpoSettings(cachedPropertySettings.dpoSettings);
          }
        }
        
        // Restore outlets from cache
        const cachedOutlets = await offlineDB.getCachedData('outlets');
        if (cachedOutlets) setOutlets(cachedOutlets);
        
        return true;
      }
    } catch (e) {
    }
    return false;
  };

  /** Fetch fresh data from API and update cache silently */
  const refreshFromNetwork = async (isFirstLoad) => {
    try {
      // Batch 1: Core data (parallel)
      const [categoriesData, tablesData, outletsData, groupsData] = await Promise.all([
        api.get("/categories"),
        api.get("/tables").catch(() => []),
        api.get("/outlets").catch(() => []),
        api.get("/products/groups").catch(() => [])
      ]);
      setProductGroups(groupsData);
      const filteredTables = tablesData.filter(t => t.status === "available");
      setTables(filteredTables);
      await offlineDB.cacheData('tables', filteredTables, 120);
      
      // Store outlets filtered by user access
      const userOutletIds = user?.outlet_ids || [];
      const accessibleOutlets = userOutletIds.length > 0
        ? outletsData.filter(o => userOutletIds.includes(o.id))
        : outletsData;
      setOutlets(accessibleOutlets);
      await offlineDB.cacheData('outlets', accessibleOutlets, 120);
      
      // Set outlet if not already set
      let outlet = currentOutlet;
      if (!outlet && accessibleOutlets.length > 0) {
        outlet = accessibleOutlets[0];
        setCurrentOutlet(outlet);
      }
      
      // Batch 2: Products + categories (depends on outlet)
      if (outlet?.id) {
        try {
          const menuData = await api.get(`/outlets/${outlet.id}/menu`);
          const posProducts = menuData.map(p => ({
            ...p,
            price: p.effective_price ?? p.price
          }));
          setProducts(posProducts);
          await cacheProducts(posProducts);
        } catch (e) {
          const productsData = await api.get("/products?for_pos=true");
          setProducts(productsData);
          await cacheProducts(productsData);
        }
        
        const visibleCategories = categoriesData.filter(cat => {
          const vis = cat.visible_outlet_ids || [];
          return vis.length === 0 || vis.includes(outlet.id);
        });
        setCategories(visibleCategories);
        await cacheCategories(visibleCategories);
      } else {
        const productsData = await api.get("/products?for_pos=true");
        setProducts(productsData);
        await cacheProducts(productsData);
        setCategories(categoriesData);
        await cacheCategories(categoriesData);
      }
      
      // Batch 3: Outlet settings + template + property (parallel where possible)
      if (outlet?.id) {
        try {
          const outletDetails = await api.get(`/outlets/${outlet.id}`);
          setOutletSettings(outletDetails);
          await offlineDB.cacheData(`outlet_${outlet.id}`, outletDetails, 120);
          
          // Receipt template
          let template = null;
          const storedDeviceId = localStorage.getItem('pos_device_id');
          if (storedDeviceId) {
            try {
              const deviceSettings = await api.get(`/mobile-devices/${storedDeviceId}/receipt-settings`);
              if (deviceSettings.source === "device") {
                template = deviceSettings.settings;
              }
            } catch (e) { /* fallback below */ }
          }
          if (!template) {
            template = await api.get(`/outlets/${outlet.id}/invoice-template`);
          }
          setInvoiceTemplate(template);
          await offlineDB.cacheData(`template_${outlet.id}`, template, 120);
          
          // Print destinations
          const destinations = await api.get(`/outlets/${outlet.id}/print-destinations`).catch(() => []);
          setPrintDestinations(destinations);
          await offlineDB.cacheData(`destinations_${outlet.id}`, destinations, 120);
          
        } catch (e) {
          setOutletSettings({ service_mode: "invoice" });
        }
      }
      
      // iMin device detection
      setIsImin(isIminDevice());
      
      // Batch 4: Property-level settings (tax, M-Pesa, card, devices)
      const propertyId = outlet?.property_id;
      if (propertyId) {
        try {
          const properties = await api.get("/properties");
          const property = properties.find(p => p.id === propertyId);
          if (property) {
            const taxMethod = property.tax_calculation_method || 'exclusive';
            setTaxMethod(taxMethod);
            const taxes = await api.get("/taxes").catch(() => []);
            const activeTaxes = taxes.filter(t => t.is_active);
            const totalRate = activeTaxes.reduce((sum, t) => sum + (t.rate || 0), 0);
            setTaxRate(totalRate || 16);
          
            const mpesaConfig = property.integrations?.mpesa;
            if (mpesaConfig?.enabled) {
              setMpesaSettings(mpesaConfig);
              setMpesaMethod(mpesaConfig.default_payment_method || 'stk_push');
            }
          
            const cybersourceConfig = property.integrations?.cybersource;
            if (cybersourceConfig?.enabled) {
              setCardSettings({ ...cybersourceConfig, property_id: propertyId });
            }

            const dpoConfig = property.integrations?.dpo;
            if (dpoConfig?.enabled) {
              setDpoSettings({ ...dpoConfig, property_id: propertyId });
            }

            const pesapalPosConfig = property.integrations?.pesapal;
            if (pesapalPosConfig?.enabled) {
              setPesapalPosSettings({ ...pesapalPosConfig, property_id: propertyId });
            }
          
            // Cache property settings for offline
            await offlineDB.cacheData('property_settings', {
              taxMethod,
              taxRate: totalRate || 16,
              mpesaSettings: mpesaConfig?.enabled ? mpesaConfig : null,
              cardSettings: cybersourceConfig?.enabled ? { ...cybersourceConfig, property_id: propertyId } : null,
              dpoSettings: dpoConfig?.enabled ? { ...dpoConfig, property_id: propertyId } : null
            }, 120);
          
            // Devices
            try {
              const devices = await api.get(`/devices?property_id=${propertyId}`);
              if (devices.length > 0) {
                const deviceForOutlet = devices.find(d => d.outlet_id === outlet?.id) || 
                                        devices.find(d => !d.outlet_id);
                if (deviceForOutlet) setCurrentDevice(deviceForOutlet);
              }
            } catch (e) { /* no devices */ }
          }
        } catch (e) {
        }
      }

      // DPO card-terminal enablement — role-agnostic endpoint (mirrors mobile), so it
      // works for waiters/cashiers even when the property integrations payload isn't available.
      if (propertyId) {
        try {
          const dpoS = await api.get(`/payments/dpo/settings?property_id=${propertyId}`);
          if (dpoS?.enabled) {
            setDpoSettings({ enabled: true, tid: dpoS.terminal_id, currency: dpoS.currency || 'KES', property_id: propertyId });
          } else {
            setDpoSettings(null);
          }
        } catch (e) { /* DPO not configured */ }
      }

      // Pesapal card-terminal enablement — role-agnostic endpoint (mirrors DPO),
      // so waiters/cashiers get it even without the full property integrations payload.
      if (propertyId) {
        try {
          const ppS = await api.get(`/payments/pesapal-pos/settings?property_id=${propertyId}`);
          if (ppS?.enabled) {
            setPesapalPosSettings({ enabled: true, terminal_sn: ppS.terminal_sn, currency: ppS.currency || 'KES', property_id: propertyId });
          } else {
            setPesapalPosSettings(null);
          }
        } catch (e) { /* Pesapal not configured */ }
      }
      
      // Fetch shift and invoices
      fetchCurrentShift();
      await fetchInvoices();
      
      if (!isFirstLoad) {
      }
    } catch (error) {
      if (isFirstLoad) {
        toast.error("Connection error. Using cached data if available.");
      }
    }
  };

  const fetchInvoices = async () => {
    try {
      const [open, closed] = await Promise.all([
        api.get("/invoices/my-open"),
        api.get("/invoices/closed?limit=20")
      ]);
      setOpenInvoices(open);
      setClosedInvoices(closed);
    } catch (error) {
    }
  };

  const searchCustomer = async () => {
    if (!customerSearch) return;
    try {
      const customer = await api.get(`/customers/phone/${customerSearch}`);
      setSelectedCustomer(customer);
      setCustomerInfo(customer.name, customer.phone);
      toast.success(`Found: ${customer.name} (${customer.loyalty_points} pts)`);
    } catch {
      toast.info("Customer not found.");
      setSelectedCustomer(null);
    }
  };

  // Fetch customers for selection sheet
  const fetchCustomers = async (search = "") => {
    try {
      setCustomersLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("limit", "20");
      const customers = await api.get(`/customers?${params.toString()}`);
      setCustomersList(Array.isArray(customers) ? customers : customers.customers || []);
    } catch (error) {
      setCustomersList([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Handle customer selection from sheet
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerInfo(customer.name, customer.phone);
    setCustomerSheetOpen(false);
    toast.success(`Selected: ${customer.name}`);
  };

  // Clear selected customer
  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setCustomerInfo("", "");
    setCreditCheckResult(null);
  };

  // Check credit availability for credit sales
  const checkCreditAvailability = async (amount) => {
    if (!selectedCustomer) {
      setCreditCheckResult({ allowed: false, reason: "No customer selected" });
      return false;
    }
    
    if (!selectedCustomer.is_debtor) {
      setCreditCheckResult({ allowed: false, reason: "Customer is not set up as a debtor" });
      return false;
    }
    
    try {
      const result = await api.post(`/customers/${selectedCustomer.id}/check-credit?amount=${amount}`);
      setCreditCheckResult(result);
      return result.allowed;
    } catch (error) {
      setCreditCheckResult({ allowed: false, reason: "Failed to check credit" });
      return false;
    }
  };

  // Request credit approval for amounts exceeding limit
  const requestCreditApproval = async (invoiceId, amount) => {
    try {
      const approval = await api.post("/credit-approvals/request", {
        customer_id: selectedCustomer.id,
        invoice_id: invoiceId,
        amount: amount,
        reason: `Credit sale exceeds available credit. Shortfall: ${creditCheckResult?.shortfall || 0}`
      });
      setPendingCreditApproval(approval);
      setCreditApprovalPending(true);
      toast.info("Credit approval requested. Waiting for manager approval.");
      return approval;
    } catch (error) {
      toast.error("Failed to request credit approval");
      return null;
    }
  };

  const calculateLoyaltyDiscount = (points) => {
    if (!selectedCustomer || points <= 0) {
      setLoyaltyDiscount(0);
      setRedeemPoints(0);
      return;
    }
    const maxPoints = Math.min(points, selectedCustomer.loyalty_points);
    setRedeemPoints(maxPoints);
    setLoyaltyDiscount(maxPoints);
  };

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error("Please enter a discount code");
      return;
    }
    
    if (!canApplyDiscount) {
      toast.error("You don't have permission to apply discounts");
      return;
    }
    
    try {
      const subtotal = activeInvoice ? activeInvoice.subtotal : getSubtotal();
      const total = activeInvoice ? activeInvoice.total : getTotal();
      
      if (subtotal <= 0 && total <= 0) {
        toast.error("Add items before applying discount");
        return;
      }
      
      // Pass tax method and total for proper discount calculation
      // Tax Inclusive: discount on total, Tax Exclusive: discount on subtotal
      const currentTaxMethod = taxCalculationMethod || "exclusive";
      const params = new URLSearchParams({
        code: discountCode,
        subtotal: subtotal.toString(),
        total: total.toString(),
        tax_method: currentTaxMethod
      });
      
      const result = await api.post(`/discounts/validate?${params}`);
      setAppliedDiscount(result);
      setPromoDiscount(result.discount_amount);
      
      // Show which base the discount was applied on
      const appliedOn = result.applied_on === "total" ? "grand total" : "subtotal";
      toast.success(`Discount applied on ${appliedOn}: -${formatCurrency(result.discount_amount)}`);
    } catch (error) {
      const errorMsg = error.message || "Invalid discount code";
      toast.error(errorMsg);
      setAppliedDiscount(null);
      setPromoDiscount(0);
    }
  };

  // Filter products: Only show products with selling price > 0
  const sellableProducts = products.filter((p) => p.price && p.price > 0);
  
  // Filter categories: Only show categories that have at least one sellable product
  // Also filter by selected product group if one is selected
  const categoriesWithProducts = categories.filter((cat) => {
    const hasProducts = sellableProducts.some((p) => p.category_id === cat.id);
    if (!hasProducts) return false;
    if (selectedGroup === "all") return true;
    return cat.group_id === selectedGroup;
  });
  
  // Get unique groups that have categories with products
  const activeGroups = productGroups.filter(group => 
    categories.some(cat => 
      cat.group_id === group.id && 
      sellableProducts.some(p => p.category_id === cat.id)
    )
  );
  
  // Filter products by selected category (and implicitly by group through category)
  const filteredProducts = selectedCategory === "all"
    ? (selectedGroup === "all" 
        ? sellableProducts 
        : sellableProducts.filter(p => {
            const cat = categories.find(c => c.id === p.category_id);
            return cat && cat.group_id === selectedGroup;
          }))
    : sellableProducts.filter((p) => p.category_id === selectedCategory);

  // Handle outlet switch — full context reload
  const handleOutletSwitch = async (newOutlet) => {
    if (newOutlet.id === currentOutlet?.id) return;
    
    // Warn if cart has items
    if (items.length > 0) {
      const confirmed = window.confirm(
        `Switching to ${newOutlet.name} will clear your current cart (${items.length} items). Continue?`
      );
      if (!confirmed) return;
    }
    
    setOutletSwitching(true);
    try {
      // Clear cart
      clearCart();
      setSelectedCustomer(null);
      setSelectedTable(null);
      setSelectedGroup("all");
      setSelectedCategory("all");
      
      // Set new outlet
      setCurrentOutlet(newOutlet);
      
      // Reload outlet-specific menu
      try {
        const menuData = await api.get(`/outlets/${newOutlet.id}/menu`);
        const posProducts = menuData.map(p => ({
          ...p,
          price: p.effective_price ?? p.price
        }));
        setProducts(posProducts);
        await cacheProducts(posProducts);
      } catch (e) {
        const productsData = await api.get("/products?for_pos=true");
        setProducts(productsData);
      }
      
      // Reload categories filtered by outlet visibility
      const categoriesData = await api.get("/categories");
      const visibleCategories = categoriesData.filter(cat => {
        const vis = cat.visible_outlet_ids || [];
        return vis.length === 0 || vis.includes(newOutlet.id);
      });
      setCategories(visibleCategories);
      
      // Reload shift for new outlet
      try {
        const shifts = await api.get("/shifts?status=active");
        const outletShift = shifts.find(s => s.outlet_id === newOutlet.id);
        setCurrentShift(outletShift || null);
      } catch (e) {
        setCurrentShift(null);
      }
      
      // Reload outlet settings
      try {
        const outletData = await api.get(`/outlets/${newOutlet.id}`);
        setOutletSettings(outletData.settings || {});
      } catch (e) {}
      
      // Reload invoices for new outlet
      fetchInvoices();
      
      toast.success(`Switched to ${newOutlet.name}`);
    } catch (error) {
      toast.error("Failed to switch outlet");
    } finally {
      setOutletSwitching(false);
    }
  };

  // Reset category selection when group changes
  const handleGroupChange = (groupId) => {
    setSelectedGroup(groupId);
    setSelectedCategory("all"); // Reset to "all" categories within the new group
  };

  // Auto-refresh invoices when new QR orders come in
  useEffect(() => {
    if (qrPendingCount > 0) fetchInvoices();
  }, [qrPendingCount]);


  const isInvoiceMode = outletSettings.service_mode === "invoice";

  // Check if product has customization options
  const productHasCustomization = (product) => {
    return (
      product.prep_instruction_enabled ||
      (product.option_group_ids && product.option_group_ids.length > 0) ||
      (product.inline_options && product.inline_options.length > 0) ||
      product.meal_type === "meal_slot" ||
      product.meal_type === "meal_component"
    );
  };

  // Handle product click - check if customization needed
  const handleProductClick = (product) => {
    if (navigator.vibrate) navigator.vibrate(10);
    
    if (productHasCustomization(product)) {
      setProductToCustomize(product);
      setCustomizationModalOpen(true);
    } else {
      addItem(product);
    }
  };

  // Handle adding customized product to cart
  const handleAddCustomizedProduct = (customizedProduct, quantity) => {
    // Add item with customizations
    for (let i = 0; i < quantity; i++) {
      addItem({
        ...customizedProduct,
        // Use customized price as the display price
        price: customizedProduct.customized_price || customizedProduct.price
      });
    }
    
    // Show confirmation
    const optionsCount = customizedProduct.selected_options?.length || 0;
    if (optionsCount > 0 || customizedProduct.prep_options_selected?.length > 0) {
      toast.success(`Added ${customizedProduct.name} with ${optionsCount} modifier${optionsCount !== 1 ? 's' : ''}`);
    }
  };

  // INVOICE MODE: Confirm Order (sends to printer)
  const handleConfirmOrder = async () => {
    if (items.length === 0 && !activeInvoice) {
      toast.error("Add items first");
      return;
    }

    setProcessing(true);
    try {
      let invoiceId = activeInvoice?.id;
      let itemsToSend = [];
      
      // If no active invoice, create one first
      if (!invoiceId) {
        const invoiceData = {
          outlet_id: currentOutlet?.id || "default",
          property_id: currentOutlet?.property_id || "default",
          items: items.map((item) => ({
            product_id: item.product_id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            product_type: item.product_type || "food",
            // Customization data
            prep_instructions: item.prep_instructions || "",
            prep_options_selected: item.prep_options_selected || [],
            selected_options: item.selected_options || [],
            options_total: item.options_total || 0,
            // Meal/Combo data
            meal_selections: item.meal_selections || [],
            is_meal: item.is_meal || false
          })),
          customer_name: selectedCustomer?.name || customerName || "Walk-in",
          customer_phone: selectedCustomer?.phone || customerPhone || "",
          customer_id: selectedCustomer?.id,
          order_type: orderType,
          table_id: selectedTable?.id,
        };
        const newInvoice = await api.post("/invoices", invoiceData);
        invoiceId = newInvoice.id;
        itemsToSend = items;
        toast.success(`Invoice created: ${newInvoice.invoice_number}`);
      } else if (items.length > 0) {
        // Add new items to existing invoice
        const newItems = items.map((item) => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          product_type: item.product_type || "food",
          // Customization data
          prep_instructions: item.prep_instructions || "",
          prep_options_selected: item.prep_options_selected || [],
          selected_options: item.selected_options || [],
          options_total: item.options_total || 0,
          // Meal/Combo data
          meal_selections: item.meal_selections || [],
          is_meal: item.is_meal || false
        }));
        await api.post(`/invoices/${invoiceId}/add-items`, newItems);
        itemsToSend = items;
        toast.success("Items added to invoice");
      }
      
      // Send to printer - for new items or if invoice is still open
      if (itemsToSend.length > 0 || activeInvoice?.status === "open") {
        // Determine print destinations based on new items
        const hasFood = itemsToSend.some(item => (item.product_type || "food") !== "drinks" && item.product_type !== "beverage");
        const hasDrinks = itemsToSend.some(item => item.product_type === "drinks" || item.product_type === "beverage");
        
        // Find configured print destinations for kitchen and bar
        const kitchenDest = printDestinations.find(d => d.destination_type === "kitchen");
        const barDest = printDestinations.find(d => d.destination_type === "bar");
        
        // Print to respective printers using the new device management system
        const destinationsUsed = [];
        if (hasFood || (!hasDrinks && itemsToSend.length > 0)) {
          // Use handlePrintInvoice for proper device-aware printing
          await handlePrintInvoice(invoiceId, "kitchen");
          destinationsUsed.push(kitchenDest?.name || "Kitchen");
        }
        if (hasDrinks) {
          await handlePrintInvoice(invoiceId, "bar");
          destinationsUsed.push(barDest?.name || "Bar");
        }
        
        // Update invoice status to confirmed if it was open
        if (activeInvoice?.status === "open" || !activeInvoice) {
          await api.post(`/invoices/${invoiceId}/confirm`);
        }
        
        toast.success(`Order sent to ${destinationsUsed.join(" & ") || "printer"}`);
      }
      
      clearCart();
      setCartDrawerOpen(false);
      setActiveInvoice(null);
      setSelectedTable(null);
      fetchInvoices();
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // EXPRESS MODE: Quick Checkout (supports offline)
  const handleExpressCheckout = async () => {
    if (items.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    if (paymentMethod === "mpesa" && !mpesaPhone) {
      toast.error("Enter M-Pesa phone number");
      return;
    }

    setProcessing(true);
    try {
      const orderData = {
        outlet_id: currentOutlet?.id || "default",
        items: items.map((item) => ({
          product_id: item.product_id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          product_type: item.product_type || "food",
          // Customization data
          prep_instructions: item.prep_instructions || "",
          prep_options_selected: item.prep_options_selected || [],
          selected_options: item.selected_options || [],
          options_total: item.options_total || 0,
          // Meal/Combo data
          meal_selections: item.meal_selections || [],
          is_meal: item.is_meal || false
        })),
        customer_name: selectedCustomer?.name || customerName || "Walk-in",
        customer_phone: selectedCustomer?.phone || customerPhone || "",
        customer_id: selectedCustomer?.id,
        order_type: orderType,
        payment_method: paymentMethod === "card_manual" ? "card" : paymentMethod,
        table_id: selectedTable?.id,
        redeem_points: redeemPoints,
        discount_code: appliedDiscount?.code,
      };

      // Check if online
      if (isOnline) {
        const response = await api.post("/orders", orderData);
        await api.post(`/orders/${response.id}/complete`);
        toast.success(`Order #${response.order_number} completed!`);
        
        // Print receipt if printer connected
        await printOrderReceipt(response, orderData);
      } else {
        // Create offline order
        const offlineOrder = await createOfflineOrder(
          {
            items,
            subtotal: getSubtotal(),
            tax: getTax(),
            discount: promoDiscount + loyaltyDiscount,
            total: getTotal() - promoDiscount - loyaltyDiscount,
            customerName: selectedCustomer?.name || customerName || "Walk-in",
            customerPhone: selectedCustomer?.phone || customerPhone || "",
            customerId: selectedCustomer?.id,
            tableId: selectedTable?.id,
            tableNumber: selectedTable?.number,
            orderType,
            paymentMethod
          },
          user,
          currentOutlet
        );
        
        toast.success(`Offline Order ${offlineOrder.order_number} saved! Will sync when online.`, {
          duration: 5000
        });
        
        // Print offline receipt
        await printOfflineReceipt(offlineOrder);
        
        // Update pending count
        const pending = await getPendingOfflineOrders();
        setPendingOfflineOrders(pending);
      }

      resetState();
      if (isOnline) fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Refresh M-Pesa settings from property integrations
  const refreshMpesaSettings = async () => {
    try {
      const propertyId = currentOutlet?.property_id;
      if (!propertyId) return;
      
      const integrations = await api.get(`/properties/${propertyId}/integrations`);
      const mpesaConfig = integrations?.mpesa;
      if (mpesaConfig?.enabled) {
        setMpesaSettings(mpesaConfig);
        // Only reset method if current method is c2b but c2b is not enabled
        if ((mpesaMethod === 'c2b' || mpesaMethod === 'c2b_verify') && !mpesaConfig.c2b_enabled) {
          setMpesaMethod('stk_push');
        }
      }
    } catch (error) {
    }
  };

  const startEtimsPreflight = (invoice = activeInvoice) => {
    if (!invoice?.id || !isOnline) {
      const skipped = { ready: true, skipped: true };
      etimsPreflightRef.current = null;
      return Promise.resolve(skipped);
    }

    const invoiceId = invoice.id;
    const promise = api.post(`/orders/${invoiceId}/etims/preflight`)
      .then((result) => result)
      .catch((error) => {
        const failed = { ready: false, error: error.message || "Unable to prepare eTIMS" };
        return failed;
      });

    etimsPreflightRef.current = { invoiceId, promise };
    return promise;
  };

  const ensureEtimsReadyForSettlement = async (invoice = activeInvoice) => {
    if (!invoice?.id || !isOnline) return true;

    const outstandingAmount = Math.max(0, finalTotal - (invoice.amount_paid || 0));
    const tenderedAmount = parseFloat(amountTendered) || outstandingAmount;
    if (tenderedAmount < outstandingAmount - 0.01) return true;

    let entry = etimsPreflightRef.current;
    if (!entry || entry.invoiceId !== invoice.id) {
      const promise = startEtimsPreflight(invoice);
      entry = { invoiceId: invoice.id, promise };
    }
    await entry.promise;
    return true;
  };

  // Open checkout dialog with fresh settings
  const openCheckoutDialog = async () => {
    setCartDrawerOpen(false);
    setSelectedC2BPayment(null);
    setC2bMpesaCode("");
    setC2bVerifyResult(null);
    setCheckoutOpen(true);
    if (activeInvoice?.id) startEtimsPreflight(activeInvoice);
    // Do not delay opening the modal on settings or KRA preparation. Both
    // requests run while the cashier selects the payment method.
    refreshMpesaSettings();
    // Fetch available C2B payments in background
    fetchAvailableC2BPayments();
  };

  // Sync offline orders when coming back online
  const handleSyncOfflineOrders = async () => {
    if (!isOnline || pendingOfflineOrders.length === 0) return;
    
    toast.info(`Syncing ${pendingOfflineOrders.length} offline orders...`);
    
    try {
      const results = await syncAllOfflineOrders(
        process.env.REACT_APP_BACKEND_URL,
        (progress) => {
        }
      );
      
      if (results.synced > 0) {
        toast.success(`Synced ${results.synced} orders successfully!`);
      }
      if (results.failed > 0) {
        toast.warning(`${results.failed} orders failed to sync. Will retry later.`);
      }
      
      // Refresh pending count
      const pending = await getPendingOfflineOrders();
      setPendingOfflineOrders(pending);
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error('Sync failed: ' + error.message);
    }
  };

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && pendingOfflineOrders.length > 0) {
      handleSyncOfflineOrders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // Load pending offline orders on mount
  useEffect(() => {
    const loadPendingOrders = async () => {
      const pending = await getPendingOfflineOrders();
      setPendingOfflineOrders(pending);
    };
    loadPendingOrders();
  }, []);

  // Add items to existing invoice
  const handleAddToInvoice = async () => {
    if (!activeInvoice || items.length === 0) return;

    setProcessing(true);
    try {
      const newItems = items.map((item) => ({
        product_id: item.product_id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        product_type: item.product_type || "food"
      }));

      await api.post(`/invoices/${activeInvoice.id}/add-items`, newItems);
      toast.success("Items added");
      
      clearCart();
      const updated = await api.get(`/invoices/${activeInvoice.id}`);
      setActiveInvoice(updated);
      fetchInvoices();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Void Item (permission required)
  const handleVoidItem = async () => {
    if (!activeInvoice || !voidItem) return;

    try {
      await api.post(`/invoices/${activeInvoice.id}/void-item?product_id=${voidItem.product_id}&quantity=${voidItem.quantity}&reason=${voidReason}`);
      toast.success("Item voided");
      
      const updated = await api.get(`/invoices/${activeInvoice.id}`);
      setActiveInvoice(updated);
      fetchInvoices();
      
      setVoidDialogOpen(false);
      setVoidItem(null);
      setVoidReason("");
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Fetch available C2B payments for the current invoice amount
  const fetchAvailableC2BPayments = async (searchCode = null) => {
    if (!mpesaSettings?.c2b_enabled) return;
    
    const propertyId = currentOutlet?.property_id || outletSettings?.property_id;
    if (!propertyId) return;
    
    setLoadingC2BPayments(true);
    try {
      // Build URL - don't filter by amount, let user see all available payments
      let url = `/payments/mpesa/c2b/available/${propertyId}`;
      if (searchCode && searchCode.length >= 3) {
        url += `?code_search=${searchCode}`;
      }
      const result = await api.get(url);
      setAvailableC2BPayments(result.payments || []);
    } catch (error) {
      setAvailableC2BPayments([]);
    } finally {
      setLoadingC2BPayments(false);
    }
  };

  // Verify C2B Payment Code
  const handleVerifyC2BPayment = async () => {
    if (!c2bMpesaCode.trim()) {
      toast.error("Enter M-Pesa transaction code");
      return;
    }
    
    setC2bVerifying(true);
    setC2bVerifyResult(null);
    
    try {
      const result = await api.get(`/payments/mpesa/c2b/verify/${c2bMpesaCode.trim()}`);
      setC2bVerifyResult(result);
      
      if (result.found && !result.can_use) {
        toast.warning(`This code was already used for invoice ${result.invoice_number}`);
      } else if (result.found && result.can_use) {
        toast.success(`Payment found: ${formatCurrency(result.amount)} - Ready to match`);
      } else {
        toast.info("Code not found in system - you can still use it to settle");
      }
    } catch (error) {
      toast.error("Failed to verify payment code");
      setC2bVerifyResult({ found: false, can_use: true });
    } finally {
      setC2bVerifying(false);
    }
  };

  // Settle invoice with C2B payment
  const handleSettleWithC2B = async () => {
    if (!activeInvoice) return;
    
    // Get the M-Pesa code from either selected payment or manual entry
    const mpesaCode = selectedC2BPayment?.trans_id || c2bMpesaCode.trim();
    const paymentPhone = selectedC2BPayment?.phone || "";
    
    if (!mpesaCode) {
      toast.error("Select a payment or enter M-Pesa transaction code");
      return;
    }
    
    setProcessing(true);
    try {
      const etimsReady = await ensureEtimsReadyForSettlement(activeInvoice);
      if (!etimsReady) return;

      const propertyId = currentOutlet?.property_id || outletSettings?.property_id || mpesaSettings?.property_id || "";
      const accountRef = activeInvoice.invoice_number?.slice(-4) || activeInvoice.invoice_number;
      
      // Use the finalTotal which includes discounts from the checkout dialog
      // If a C2B payment was selected, use its amount; otherwise use the calculated finalTotal
      const amountToPay = selectedC2BPayment?.amount || finalTotal;
      
      // Build the request URL with all necessary parameters
      let url = `/payments/mpesa/c2b/manual-entry?property_id=${propertyId}&mpesa_code=${mpesaCode}&amount=${amountToPay}&account_reference=${accountRef}&phone_number=${paymentPhone}&invoice_id=${activeInvoice.id}`;
      
      // Include discount code if applied
      if (appliedDiscount?.code) {
        url += `&discount_code=${appliedDiscount.code}`;
      }
      
      const result = await api.post(url);
      
      if (result.success) {
        // Build success message with eTIMS info
        let successMsg = result.message || "Invoice settled successfully!";
        if (result.etims_submitted) {
          successMsg += ` | eTIMS #${result.etims_invc_no}`;
        }
        toast.success(successMsg);
        
        // Auto-print receipt with eTIMS QR code
        if (result.auto_print && result.print_data) {
          await handleAutoPrint(result.print_data);
        }
        
        // Reset state
        resetState();
        setC2bMpesaCode("");
        setC2bVerifyResult(null);
        setSelectedC2BPayment(null);
        setAvailableC2BPayments([]);
        fetchInvoices();
        setCheckoutOpen(false);
        setActiveInvoice(null);
      } else {
        toast.error(result.warning || result.message || "Failed to settle invoice");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to settle invoice";
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  // Close Invoice (Settlement)
  const handleCloseInvoice = async () => {
    
    if (!activeInvoice) {
      return;
    }

    // For M-Pesa STK Push, require phone number (only when configured for real-time)
    if (paymentMethod === "mpesa" && mpesaSettings?.enabled && mpesaMethod === "stk_push" && !mpesaPhone) {
      toast.error("Enter M-Pesa phone number");
      return;
    }
    
    // For M-Pesa manual mode (not configured), require reference
    if (paymentMethod === "mpesa" && !mpesaSettings?.enabled && !cardDetails.manual_ref?.trim()) {
      toast.error("Enter M-Pesa transaction reference");
      return;
    }
    
    // For M-Pesa C2B verification, use the C2B settlement flow
    if (paymentMethod === "mpesa" && mpesaMethod === "c2b_verify") {
      await handleSettleWithC2B();
      return;
    }
    
    // For M-Pesa C2B (show instructions), show the C2B dialog with payment instructions
    if (paymentMethod === "mpesa" && mpesaMethod === "c2b") {
      setC2bInvoice(activeInvoice);
      setC2bDialogOpen(true);
      return;
    }
    
    // For Card payments with real-time integration, validate card details
    if (paymentMethod === "card") {
      if (!cardDetails.card_number || cardDetails.card_number.replace(/\s/g, '').length < 15) {
        toast.error("Enter valid card number");
        return;
      }
      if (!cardDetails.expiry_month || !cardDetails.expiry_year) {
        toast.error("Enter card expiry date");
        return;
      }
      if (!cardDetails.cvv || cardDetails.cvv.length < 3) {
        toast.error("Enter valid CVV");
        return;
      }
      if (!cardDetails.cardholder_name) {
        toast.error("Enter cardholder name");
        return;
      }
    }
    
    // For Card payments without integration (manual), validate last 4 + reference
    if (paymentMethod === "card_manual") {
      if (!cardDetails.card_last4 || cardDetails.card_last4.length < 4) {
        toast.error("Enter last 4 digits of card");
        return;
      }
      if (!cardDetails.manual_ref?.trim()) {
        toast.error("Enter card transaction reference");
        return;
      }
    }
    
    // For Credit payments, validate customer and credit availability
    if (paymentMethod === "credit") {
      if (!selectedCustomer) {
        toast.error("Please select a customer for credit sale");
        return;
      }
      if (!selectedCustomer.is_debtor) {
        toast.error("This customer is not set up as a debtor");
        return;
      }
      // Check credit availability
      const creditAllowed = await checkCreditAvailability(activeInvoice.total);
      if (!creditAllowed && !creditCheckResult?.requires_approval) {
        toast.error("Insufficient credit available");
        return;
      }
      // If requires approval and not yet approved, request approval
      if (creditCheckResult?.requires_approval && !creditApprovalPending) {
        const approval = await requestCreditApproval(activeInvoice.id, activeInvoice.total);
        if (approval) {
          toast.info("Credit approval requested. Please wait for manager approval.");
          return;
        }
      }
    }
    
    setProcessing(true);
    const etimsReady = await ensureEtimsReadyForSettlement(activeInvoice);
    if (!etimsReady) {
      setProcessing(false);
      return;
    }
    try {
      let result;
      
      // For card payments with real-time integration
      if (paymentMethod === "card") {
        const propertyId = cardSettings?.property_id || currentOutlet?.property_id || outletSettings?.property_id;
        const cardPaymentResult = await api.post(`/payments/card/process?property_id=${propertyId}`, {
          card_number: cardDetails.card_number.replace(/\s/g, ''),
          expiry_month: cardDetails.expiry_month,
          expiry_year: cardDetails.expiry_year.length === 2 ? `20${cardDetails.expiry_year}` : cardDetails.expiry_year,
          cvv: cardDetails.cvv,
          cardholder_name: cardDetails.cardholder_name,
          amount: activeInvoice.total,
          currency: activeInvoice.currency || "KES",
          order_id: activeInvoice.id,
          customer_email: activeInvoice.customer_email || selectedCustomer?.email
        });
        
        if (!cardPaymentResult.success) {
          throw new Error(cardPaymentResult.error || "Card payment failed");
        }
        
        // Now close the invoice with card payment method
        result = await api.post(
          `/invoices/${activeInvoice.id}/close?payment_method=card&card_transaction_id=${cardPaymentResult.transaction_id}&loyalty_points_used=${redeemPoints}&discount_code=${appliedDiscount?.code || ""}&amount_tendered=${amountTendered || 0}`
        );
        
        // Clear card details after successful payment
        setCardDetails({
          card_number: "",
          expiry_month: "",
          expiry_year: "",
          cvv: "",
          cardholder_name: ""
        });
      } else if (paymentMethod === "card_manual") {
        // Manual card payment - no real-time processing
        const manualRef = cardDetails.manual_ref?.trim() || "";
        const last4 = cardDetails.card_last4?.trim() || "";
        result = await api.post(
          `/invoices/${activeInvoice.id}/close?payment_method=card&manual_ref=${encodeURIComponent(manualRef)}&card_last4=${last4}&loyalty_points_used=${redeemPoints}&discount_code=${appliedDiscount?.code || ""}&amount_tendered=${amountTendered || 0}`
        );
        
        // Clear manual details after successful payment
        setCardDetails({
          card_number: "",
          expiry_month: "",
          expiry_year: "",
          cvv: "",
          cardholder_name: "",
          card_last4: "",
          manual_ref: ""
        });
      } else if (paymentMethod === "nfc") {
        // For NFC payments, create a payment request and wait for it to be processed
        const propertyId = cardSettings?.property_id || currentOutlet?.property_id || outletSettings?.property_id;
        
        setNfcPaymentStatus("pending");
        
        // Create NFC payment request
        const nfcRequest = await api.post(`/nfc-payments/request`, {
          invoice_id: activeInvoice.id,
          property_id: propertyId,
          amount: activeInvoice.total,
          currency: activeInvoice.currency || "KES",
          require_pin: true,
          description: `Invoice ${activeInvoice.id}`,
          customer_name: selectedCustomer?.name || activeInvoice.customer_name
        });
        
        if (!nfcRequest.payment_request_id) {
          throw new Error("Failed to create NFC payment request");
        }
        
        setNfcPaymentRequestId(nfcRequest.payment_request_id);
        setNfcPaymentStatus("waiting");
        
        // If running on Android with NFC bridge, trigger the payment
        if (typeof window !== 'undefined' && window.HostPointNFCBridge?.requestNfcPayment) {
          window.HostPointNFCBridge.requestNfcPayment({
            payment_request_id: nfcRequest.payment_request_id,
            invoice_id: activeInvoice.id,
            amount: activeInvoice.total,
            currency: activeInvoice.currency || "KES",
            require_pin: true
          });
        }
        
        // Poll for NFC payment status
        let attempts = 0;
        const maxAttempts = 120; // 2 minutes
        
        const checkNfcStatus = setInterval(async () => {
          attempts++;
          try {
            const status = await api.get(`/nfc-payments/request/${nfcRequest.payment_request_id}`);
            
            if (status.status === "processing") {
              setNfcPaymentStatus("processing");
            } else if (status.status === "authorized" || status.status === "captured") {
              clearInterval(checkNfcStatus);
              setNfcPollingInterval(null);
              setNfcPaymentStatus("success");
              
              // Close the invoice with NFC card payment
              result = await api.post(
                `/invoices/${activeInvoice.id}/close?payment_method=card_nfc&card_transaction_id=${status.transaction_id || nfcRequest.payment_request_id}&loyalty_points_used=${redeemPoints}&discount_code=${appliedDiscount?.code || ""}`
              );
              
              toast.success("NFC Payment successful!");
              
              // Reset and continue
              setTimeout(() => {
                setNfcPaymentStatus("idle");
                setNfcPaymentRequestId(null);
                setActiveInvoice(null);
              }, 1500);
              
              setProcessing(false);
              return;
            } else if (status.status === "declined" || status.status === "failed" || status.status === "cancelled") {
              clearInterval(checkNfcStatus);
              setNfcPollingInterval(null);
              setNfcPaymentStatus("failed");
              toast.error(status.error_message || "NFC Payment failed");
              setProcessing(false);
              return;
            }
          } catch (err) {
          }
          
          if (attempts >= maxAttempts) {
            clearInterval(checkNfcStatus);
            setNfcPollingInterval(null);
            setNfcPaymentStatus("failed");
            toast.error("NFC Payment timed out");
            setProcessing(false);
          }
        }, 1000);
        
        setNfcPollingInterval(checkNfcStatus);
        return; // Exit early, polling will handle the rest
      } else if (paymentMethod === "dpo_card") {
        // DPO Push to Pay - push the bill total to the physical card terminal
        result = await api.post(`/payments/dpo/start/${activeInvoice.id}`);
        if (result?.status === "pending") {
          setDpoInvoice({ id: activeInvoice.id, total: getTotal(), number: activeInvoice.invoice_number || activeInvoice.order_number });
          setCheckoutOpen(false);
          setShowDpoDialog(true);
          fetchInvoices();
          setProcessing(false);
          return;
        }
      } else if (paymentMethod === "dpo_mpesa") {
        // Network (DPO) M-Pesa - push a MoMo prompt to the customer's phone via terminal
        if (!mpesaPhone) {
          toast.error("Enter the customer's M-Pesa number");
          setProcessing(false);
          return;
        }
        result = await api.post(`/payments/dpo/mpesa/start/${activeInvoice.id}?phone=${encodeURIComponent(mpesaPhone)}`);
        if (result?.status === "pending") {
          setDpoInvoice({ id: activeInvoice.id, total: getTotal(), number: activeInvoice.invoice_number || activeInvoice.order_number, channel: "mpesa" });
          setCheckoutOpen(false);
          setShowDpoDialog(true);
          fetchInvoices();
          setProcessing(false);
          return;
        }
      } else if (paymentMethod === "pesapal_pos") {
        // Pesapal Push to Pay - push the bill total to the physical card terminal
        result = await api.post(`/payments/pesapal-pos/start/${activeInvoice.id}`);
        if (result?.status === "pending") {
          setPesapalPosInvoice({ id: activeInvoice.id, total: getTotal(), number: activeInvoice.invoice_number || activeInvoice.order_number, channel: "card" });
          setCheckoutOpen(false);
          setShowPesapalPosDialog(true);
          fetchInvoices();
          setProcessing(false);
          return;
        }
      } else if (paymentMethod === "pesapal_mpesa") {
        // Pesapal M-Pesa - send an STK push to the customer's phone via Pesapal
        if (!mpesaPhone) {
          toast.error("Enter the customer's M-Pesa number");
          setProcessing(false);
          return;
        }
        result = await api.post(`/payments/pesapal-pos/start/${activeInvoice.id}?method=mpesa&phone=${encodeURIComponent(mpesaPhone)}`);
        if (result?.status === "pending") {
          setPesapalPosInvoice({ id: activeInvoice.id, total: getTotal(), number: activeInvoice.invoice_number || activeInvoice.order_number, channel: "mpesa" });
          setCheckoutOpen(false);
          setShowPesapalPosDialog(true);
          fetchInvoices();
          setProcessing(false);
          return;
        }
      } else if (paymentMethod === "credit") {
        // Credit sale - post to customer account
        result = await api.post(
          `/invoices/${activeInvoice.id}/close?payment_method=credit&customer_id=${selectedCustomer.id}&loyalty_points_used=${redeemPoints}&discount_code=${appliedDiscount?.code || ""}&amount_tendered=${amountTendered || 0}`
        );
        
        if (result.status === "credit_posted") {
          toast.success(`Invoice posted to ${selectedCustomer.name}'s account`);
          // Reset credit state
          setCreditCheckResult(null);
          setCreditApprovalPending(false);
          setPendingCreditApproval(null);
        }
      } else {
        // Cash, M-Pesa, or other payment methods
        const manualRef = cardDetails.manual_ref?.trim() || "";
        let closeUrl = `/invoices/${activeInvoice.id}/close?payment_method=${paymentMethod}&mpesa_phone=${mpesaPhone}&loyalty_points_used=${redeemPoints}&discount_code=${appliedDiscount?.code || ""}&amount_tendered=${amountTendered || 0}`;
        if (paymentMethod === "mpesa" && manualRef) {
          closeUrl += `&manual_ref=${encodeURIComponent(manualRef)}`;
        }
        result = await api.post(closeUrl);
        
        // Clear manual ref if used
        if (manualRef) {
          setCardDetails(prev => ({...prev, manual_ref: ""}));
        }
      }
      
      
      // Handle M-Pesa STK Push payment - need to wait for confirmation
      if (paymentMethod === "mpesa" && result.status === "pending") {
        toast.info("M-Pesa payment initiated. Please check your phone to complete payment.");
        
        // Poll for payment status (runs async via setTimeout, manages its own processing state)
        const checkoutRequestId = result.checkout_request_id;
        let attempts = 0;
        const maxAttempts = 60; // 60 seconds timeout
        
        // Keep processing=true for STK push flow (polling handles cleanup)
        const pollPaymentStatus = async () => {
          attempts++;
          try {
            const statusResult = await api.get(`/payments/mpesa/status/${checkoutRequestId}`);
            
            if (statusResult.status === "completed") {
              toast.success(`Payment successful! M-Pesa Receipt: ${statusResult.mpesa_receipt_number || 'Received'}`);
              
              try {
                const printResult = await api.post(`/invoices/${activeInvoice.id}/print?destination=receipt`);
                if (printResult.print_data) {
                  await handleAutoPrint(printResult.print_data);
                }
              } catch (printError) {
              }
              
              resetState();
              fetchInvoices();
              setCheckoutOpen(false);
              setActiveInvoice(null);
              setProcessing(false);
              return;
            } else if (statusResult.status === "failed" || statusResult.status === "cancelled") {
              toast.error(`Payment ${statusResult.status}: ${statusResult.result_desc || 'Please try again'}`);
              fetchInvoices();
              setProcessing(false);
              return;
            }
            
            if (attempts < maxAttempts) {
              setTimeout(pollPaymentStatus, 1000);
            } else {
              toast.warning("Payment verification timed out. Please check payment status manually.");
              fetchInvoices();
              setProcessing(false);
            }
          } catch (error) {
            if (attempts < maxAttempts) {
              setTimeout(pollPaymentStatus, 1000);
            } else {
              toast.error("Failed to verify payment status");
              setProcessing(false);
            }
          }
        };
        
        setTimeout(pollPaymentStatus, 3000);
        // Don't reset processing here - polling manages it
        return;
      }
      
      // For cash/card payments, complete immediately
      // Check if it was a partial payment
      if (result.is_partial) {
        toast.success(`Partial payment of ${formatCurrency(result.amount_tendered)} received. Outstanding: ${formatCurrency(result.balance_due)}`);
        fetchInvoices();
        setCheckoutOpen(false);
        setActiveInvoice(null);
        setProcessing(false);
        return;
      }

      let etimsMsg = "";
      if (result.etims_submitted) {
        etimsMsg = ` | eTIMS #${result.etims_invc_no}`;
      }
      toast.success(`Invoice closed!${etimsMsg} ${result.points_earned > 0 ? `+${result.points_earned} pts` : ''}`);
      if (result.etims_error) {
        toast.warning(`Sale saved, but KRA did not accept it: ${result.etims_error}`);
      }
      
      // Auto-print receipt with eTIMS QR code
      if (result.auto_print && result.print_data) {
        try {
          await handleAutoPrint(result.print_data);
        } catch (printError) {
          // Payment/settlement has already succeeded. A local printer problem
          // must not leave the POS in a processing state or invite a retry.
          toast.warning("Sale settled, but the receipt could not be printed. Reprint it from Closed Invoices.");
        }
      }
      
      resetState();
      fetchInvoices();
      setCheckoutOpen(false);
      setActiveInvoice(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };
  
  // Handle C2B payment confirmation (mark as awaiting payment)
  const handleC2BPayment = async () => {
    if (!c2bInvoice) return;
    
    setProcessing(true);
    try {
      // Confirm the invoice to mark it as awaiting C2B payment
      await api.post(`/invoices/${c2bInvoice.id}/confirm`);
      
      toast.success("Invoice confirmed. Waiting for M-Pesa payment.");
      setC2bDialogOpen(false);
      setC2bInvoice(null);
      resetState();
      fetchInvoices();
      setCheckoutOpen(false);
      setActiveInvoice(null);
    } catch (error) {
      toast.error(error.message || "Failed to confirm invoice");
    } finally {
      setProcessing(false);
    }
  };

  // Cancel Invoice
  // Close a zero-balance invoice (after all items have been voided)
  const handleCloseZeroBalanceInvoice = async (invoiceId) => {
    // Verify it's a zero-balance invoice
    if (activeInvoice && (activeInvoice.total || 0) > 0) {
      toast.error("Cannot close invoice with balance. Void all items first or settle the payment.");
      return;
    }
    
    if (!confirm("Close this invoice? All items have been voided.")) return;
    try {
      await api.post(`/invoices/${invoiceId}/cancel?reason=Closed - all items voided`);
      toast.success("Invoice closed");
      fetchInvoices();
      setActiveInvoice(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetState = () => {
    clearCart();
    setCheckoutOpen(false);
    setCartDrawerOpen(false);
    setMpesaPhone("");
    setSelectedCustomer(null);
    setSelectedTable(null);
    setRedeemPoints(0);
    setLoyaltyDiscount(0);
    setCustomerSearch("");
    setDiscountCode("");
    setAppliedDiscount(null);
    setPromoDiscount(0);
  };

  const handleLogout = () => {
    logout();
    navigate("/pin-login");
  };

  // Shift Management Functions
  const fetchCurrentShift = async () => {
    try {
      // Pass outlet_id to get the correct shift for this outlet
      const outletId = currentOutlet?.id;
      const shift = await api.get(`/shifts/current${outletId ? `?outlet_id=${outletId}` : ''}`);
      setCurrentShift(shift);
      if (shift?.business_date) {
        setBusinessDate(shift.business_date);
      }
    } catch {
      setCurrentShift(null);
    }
    
    // Also try to get business date from property if no shift
    if (!businessDate && currentOutlet?.property_id) {
      try {
        const eodStatus = await api.get(`/eod/status/${currentOutlet.property_id}`);
        if (eodStatus?.current_business_date) {
          setBusinessDate(eodStatus.current_business_date);
        }
      } catch {
        // Use today's date as fallback
        setBusinessDate(new Date().toISOString().split('T')[0]);
      }
    }
  };

  const handleCloseShift = async () => {
    if (!currentShift) return;
    setShiftLoading(true);
    try {
      const params = new URLSearchParams({
        closing_cash: closingCash || "0",
        bank_deposit: bankDeposit || "0",
        float_retained: floatRetained || "0",
        notes: shiftNotes
      });
      
      const result = await api.post(`/shifts/${currentShift.id}/close?${params}`);
      toast.success(`Shift closed. Total sales: ${formatCurrency(result.total_sales)}`);
      
      if (result.cash_difference !== 0) {
        if (result.cash_difference > 0) {
          toast.info(`Cash overage: ${formatCurrency(result.cash_difference)}`);
        } else {
          toast.warning(`Cash shortage: ${formatCurrency(Math.abs(result.cash_difference))}`);
        }
      }
      
      // Auto-print shift summary to bill printer
      await printShiftSummary(currentShift.id);
      
      setShiftDialogOpen(false);
      setClosingCash("");
      setBankDeposit("");
      setFloatRetained("");
      setShiftNotes("");
      fetchCurrentShift();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setShiftLoading(false);
    }
  };

  const openInvoiceForEdit = async (invoice) => {
    setActiveInvoice(invoice);
    setInvoicesOpen(false);
    if (invoice.customer_id) {
      // Fetch full customer details to get is_debtor status
      try {
        const customer = await api.get(`/customers/${invoice.customer_id}`);
        setSelectedCustomer(customer);
      } catch (e) {
        // Fallback to basic info if customer fetch fails
        setSelectedCustomer({ id: invoice.customer_id, name: invoice.customer_name, phone: invoice.customer_phone });
      }
    }
  };

  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  // Round final total to nearest whole number for M-Pesa compatibility
  const finalTotal = Math.round((activeInvoice ? activeInvoice.total : getTotal()) - loyaltyDiscount - promoDiscount);

  const getStatusBadge = (status) => {
    const badges = {
      open: <Badge variant="outline" className="text-blue-500 border-blue-500"><Clock className="h-3 w-3 mr-1" />Open</Badge>,
      confirmed: <Badge className="bg-orange-500"><Send className="h-3 w-3 mr-1" />Sent</Badge>,
      completed: <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Closed</Badge>,
      cancelled: <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>
    };
    return badges[status] || <Badge variant="secondary">{status}</Badge>;
  };

  // Cart Content Component
  // NOTE: This is technical debt - CartContent should be extracted to a separate file
  // but doing so requires passing 50+ props. Planned for future refactoring.
  // eslint-disable-next-line react/no-unstable-nested-components
  const CartContent = ({ inDrawer = false }) => (
    <>
      <div className={cn("p-4 border-b", inDrawer && "pt-0")}>
        <div className="flex items-center justify-between">
          <h2 className="font-semibold font-[Manrope] flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            {activeInvoice ? `Invoice #${activeInvoice.invoice_number?.slice(-8)}` : "New Order"}
            {cartItemCount > 0 && !activeInvoice && <Badge variant="secondary">{cartItemCount}</Badge>}
          </h2>
          {activeInvoice && (
            <Button variant="ghost" size="sm" onClick={() => setActiveInvoice(null)}><X className="h-4 w-4" /></Button>
          )}
        </div>
        
        {/* Table & Customer Selection */}
        {!activeInvoice && (
          <div className="mt-3 space-y-3">
            {/* Table & Customer Row */}
            <div className="flex gap-2">
              {/* Table Selection - Show when Dine In */}
              {orderType === "dine_in" && (
                <Select 
                  value={selectedTable?.id || "none"} 
                  onValueChange={(val) => setSelectedTable(val === "none" ? null : tables.find(t => t.id === val))}
                >
                  <SelectTrigger className="flex-1 h-9" data-testid="table-select">
                    <SelectValue placeholder="Select Table">
                      {selectedTable ? `Table ${selectedTable.table_number}` : "No Table"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Table</SelectItem>
                    {tables.map((table) => (
                      <SelectItem key={table.id} value={table.id}>
                        Table {table.table_number} {table.capacity && `(${table.capacity} seats)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Customer Selection Button */}
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 h-9 gap-1.5"
                onClick={() => { setCustomerSheetOpen(true); fetchCustomers(); }}
                data-testid="select-customer-btn"
              >
                <User className="h-4 w-4" />
                <span className="truncate max-w-[120px]">
                  {selectedCustomer ? selectedCustomer.name : "Add Customer"}
                </span>
                {selectedCustomer && (
                  <Badge variant="secondary" className="ml-1 h-5 text-xs">
                    {selectedCustomer.loyalty_points || 0} pts
                  </Badge>
                )}
              </Button>
            </div>
            
            {/* Selected Customer Info */}
            {selectedCustomer && (
              <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{selectedCustomer.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedCustomer.phone}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={clearSelectedCustomer}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Active Invoice Info */}
        {activeInvoice && (
          <div className="mt-3 p-2 bg-primary/10 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium">Table {activeInvoice.table_number || 'N/A'}</p>
                <p className="text-xs text-muted-foreground">{activeInvoice.customer_name}</p>
              </div>
              {getStatusBadge(activeInvoice.status)}
            </div>
          </div>
        )}
      </div>

      {/* Items */}
      <ScrollArea className={cn("flex-1", inDrawer ? "max-h-[35vh]" : "")}>
        {activeInvoice ? (
          <div className="p-4 space-y-2">
            {activeInvoice.items?.map((item, idx) => {
              const itemPrice = item.price || item.unit_price || 0;
              const itemQty = item.quantity || 1;
              const itemName = item.name || item.product_name || `Item ${idx + 1}`;
              return (
                <Card key={idx} className="p-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{itemName}</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(itemPrice)} × {itemQty}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{formatCurrency(itemPrice * itemQty)}</span>
                      {activeInvoice.status !== "completed" && canVoidItems && (
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive"
                          onClick={() => { setVoidItem(item); setVoidDialogOpen(true); }}>
                          <XCircle className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
            
            {/* New items to add */}
            {items.length > 0 && (
              <>
                <Separator className="my-2" />
                <p className="text-xs text-muted-foreground font-medium">New Items:</p>
                {items.map((item, idx) => (
                  <Card key={`${item.product_id}-${item.customization_key || idx}`} className="p-2 border-dashed border-primary">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-sm text-primary">{item.name}</p>
                        {/* Show prep instructions */}
                        {item.prep_options_selected?.length > 0 && (
                          <p className="text-xs text-amber-600 font-medium">• {item.prep_options_selected.join(', ')}</p>
                        )}
                        {item.prep_instructions && (
                          <p className="text-xs text-muted-foreground italic">&quot;{item.prep_instructions}&quot;</p>
                        )}
                        {/* Show selected options */}
                        {item.selected_options?.map((opt) => (
                          <p key={opt.option_name} className="text-xs text-muted-foreground">
                            + {opt.option_name} {opt.price > 0 && <span className="text-green-600">(+{formatCurrency(opt.price)})</span>}
                          </p>
                        ))}
                        {/* Show meal selections */}
                        {item.meal_selections?.map((sel) => (
                          <p key={sel.slot_name} className="text-xs text-blue-600">
                            {sel.slot_name}: {sel.product_name}
                          </p>
                        ))}
                        <p className="text-xs text-muted-foreground">{formatCurrency(item.price)} × {item.quantity}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </>
            )}
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Cart is empty</p>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {items.map((item, idx) => (
              <Card key={`${item.product_id}-${item.customization_key || idx}`} className="p-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.name}</p>
                    {/* Show prep instructions */}
                    {item.prep_options_selected?.length > 0 && (
                      <p className="text-xs text-amber-600 font-medium">• {item.prep_options_selected.join(', ')}</p>
                    )}
                    {item.prep_instructions && (
                      <p className="text-xs text-muted-foreground italic">&quot;{item.prep_instructions}&quot;</p>
                    )}
                    {/* Show selected options */}
                    {item.selected_options?.map((opt) => (
                      <p key={opt.option_name} className="text-xs text-muted-foreground">
                        + {opt.option_name} {opt.price > 0 && <span className="text-green-600">(+{formatCurrency(opt.price)})</span>}
                      </p>
                    ))}
                    {/* Show meal selections */}
                    {item.meal_selections?.map((sel) => (
                      <p key={sel.slot_name} className="text-xs text-blue-600">
                        {sel.slot_name}: {sel.product_name}
                      </p>
                    ))}
                    <p className="text-xs text-muted-foreground">{formatCurrency(item.price)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-mono">{item.quantity}</span>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      {(items.length > 0 || activeInvoice) && (
        <div className="border-t p-4 space-y-3 bg-muted/30">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono">{formatCurrency(activeInvoice ? activeInvoice.subtotal : getSubtotal())}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground flex items-center gap-1">
                Tax 
                {taxCalculationMethod === 'inclusive' && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">incl.</span>
                )}
              </span>
              <span className="font-mono">{formatCurrency(activeInvoice ? activeInvoice.tax : getTax())}</span>
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span className="font-mono text-primary">{formatCurrency(activeInvoice ? activeInvoice.total : getTotal())}</span>
            </div>
          </div>
          
          {/* Actions based on mode and state */}
          {activeInvoice ? (
            <div className="space-y-2">
              {/* Show Confirm Order button when there are new items to send to printer */}
              {items.length > 0 && (
                <Button className="w-full h-12 bg-orange-500 hover:bg-orange-600" onClick={() => handleConfirmOrder()} disabled={processing}>
                  <Send className="mr-2 h-5 w-5" />Confirm Order (Send to Printer)
                </Button>
              )}
              {/* Show initial confirm only if invoice is open and no new items */}
              {activeInvoice.status === "open" && items.length === 0 && (
                <Button className="w-full h-12 bg-orange-500 hover:bg-orange-600" onClick={() => handleConfirmOrder()} disabled={processing}>
                  <Send className="mr-2 h-5 w-5" />Confirm Order (Print)
                </Button>
              )}
              <div className="flex gap-2">
                <Button className="flex-1 h-11" variant="outline" onClick={() => handlePrintInvoice(activeInvoice.id)}>
                  <Printer className="mr-2 h-4 w-4" />Print
                </Button>
                {/* Show Split button only if: more than 1 item line OR any item has quantity > 1 */}
                {(() => {
                  const invoiceItems = activeInvoice.items || [];
                  const hasMultipleLines = invoiceItems.length > 1;
                  const hasMultipleQuantity = invoiceItems.some(item => (item.quantity || 1) > 1);
                  const canSplit = hasMultipleLines || hasMultipleQuantity;
                  const isEmptyBill = invoiceItems.length === 0 || (activeInvoice.total || 0) === 0;
                  
                  if (isEmptyBill) {
                    // Empty/voided bill - show Close button
                    return (
                      <Button className="flex-1 h-11" variant="outline" onClick={() => handleCloseZeroBalanceInvoice(activeInvoice.id)}>
                        <XCircle className="mr-2 h-4 w-4" />Close
                      </Button>
                    );
                  } else if (canSplit && canSplitInvoice) {
                    // Can split - show Split button (only if user has permission)
                    return (
                      <Button className="flex-1 h-11" variant="outline" onClick={() => setSplitBillOpen(true)}>
                        <Split className="mr-2 h-4 w-4" />Split
                      </Button>
                    );
                  }
                  // Single item with qty 1 or no split permission - no split/close button needed
                  return null;
                })()}
                {canSettleInvoice ? (
                  <Button className="flex-1 h-11" onClick={openCheckoutDialog}>
                    <CreditCard className="mr-2 h-4 w-4" />Settle
                  </Button>
                ) : (
                  <Button className="flex-1 h-11" variant="outline" disabled title="You don't have permission to settle invoices">
                    <CreditCard className="mr-2 h-4 w-4" />Settle
                  </Button>
                )}
              </div>
            </div>
          ) : isInvoiceMode ? (
            // INVOICE MODE: Confirm Order
            <>
              {!currentShift && (
                <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-sm text-yellow-700 font-medium">⚠️ No Active Shift</p>
                  <p className="text-xs text-yellow-600">Open a shift to place orders</p>
                </div>
              )}
              {!canCreateInvoice && (
                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-center">
                  <p className="text-sm text-red-700 font-medium">⚠️ No Permission</p>
                  <p className="text-xs text-red-600">You do not have permission to create invoices</p>
                </div>
              )}
              <Button className="w-full h-14 text-lg bg-orange-500 hover:bg-orange-600" onClick={handleConfirmOrder} disabled={processing || items.length === 0 || !currentShift || !canCreateInvoice}>
                <Send className="mr-2 h-5 w-5" />
                {processing ? "Processing..." : "Confirm Order"}
              </Button>
            </>
          ) : (
            // EXPRESS MODE: Checkout
            <>
              {!currentShift && (
                <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                  <p className="text-sm text-yellow-700 font-medium">⚠️ No Active Shift</p>
                  <p className="text-xs text-yellow-600">Open a shift to place orders</p>
                </div>
              )}
              <Button className="w-full h-14 text-lg" onClick={openCheckoutDialog} disabled={items.length === 0 || !currentShift || !canCreateInvoice}>
                <CreditCard className="mr-2 h-5 w-5" />Checkout
              </Button>
            </>
          )}
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="pos-page">
      {/* Header */}
      <header className="h-14 md:h-16 border-b bg-card px-3 md:px-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 md:gap-4">
          <h1 className="text-lg md:text-xl font-bold font-[Manrope] text-primary">HostPoint POS</h1>
          {/* Outlet Switcher */}
          {outlets.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 h-8 max-w-[160px]" data-testid="outlet-switcher" disabled={outletSwitching}>
                  {outletSwitching ? <Loader2 className="h-3 w-3 animate-spin" /> : <Store className="h-3 w-3" />}
                  <span className="truncate text-xs">{currentOutlet?.name || "Select Outlet"}</span>
                  <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {outlets.map(outlet => (
                  <DropdownMenuItem
                    key={outlet.id}
                    onClick={() => handleOutletSwitch(outlet)}
                    className={outlet.id === currentOutlet?.id ? "bg-muted font-medium" : ""}
                    data-testid={`outlet-switch-${outlet.id}`}
                  >
                    <Store className="mr-2 h-4 w-4" />
                    <span>{outlet.name}</span>
                    {outlet.id === currentOutlet?.id && <Check className="ml-auto h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Badge variant={isInvoiceMode ? "default" : "outline"} className="hidden sm:flex text-xs">
              {currentOutlet?.name || (isInvoiceMode ? "Invoice" : "Express")}
            </Badge>
          )}
          {/* Network Status Indicator */}
          <button onClick={() => setShowSyncPanel(true)} className="cursor-pointer">
            <NetworkIndicator />
          </button>
          {/* Printer Status Indicator */}
          <PrinterIndicator onClick={() => setShowPrinterManager(true)} />
          {/* QR Order Notification Badge */}
          {qrPendingCount > 0 && (
            <button
              onClick={() => {
                ackQrOrders(qrPendingOrders.map(o => o.id));
                fetchInvoices();
                if (isInvoiceMode) setInvoicesOpen(true);
              }}
              className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold shadow-md hover:bg-amber-600 transition-colors"
              style={{ animation: "pulse 1s ease-in-out infinite" }}
              data-testid="qr-orders-badge"
              title="New QR menu orders — click to view invoices"
            >
              <ShoppingBag className="h-4 w-4" />
              <span>{qrPendingCount} New QR Order{qrPendingCount > 1 ? "s" : ""}</span>
            </button>
          )}
          {/* Pending Offline Orders Badge */}
          {pendingOfflineOrders.length > 0 && (
            <Badge variant="destructive" className="text-xs">
              {pendingOfflineOrders.length} pending
            </Badge>
          )}
        </div>
        
        {/* Business Date & Shift Info - Center */}
        <div className="hidden md:flex items-center gap-4 bg-muted/50 px-4 py-1.5 rounded-lg">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4 text-primary" />
            <span className="font-medium">
              {businessDate ? new Date(businessDate + 'T00:00:00').toLocaleDateString('en-GB', { 
                weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' 
              }) : 'Loading...'}
            </span>
          </div>
          <Separator orientation="vertical" className="h-5" />
          <div className="flex items-center gap-2 text-sm">
            {currentShift ? (
              <>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="font-medium">Shift {currentShift.shift_number || 1}</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground text-xs">
                  Since {new Date(currentShift.start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                <span className="text-yellow-600 font-medium">No Active Shift</span>
              </>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Invoices Button (Invoice Mode Only) */}
          {isInvoiceMode && (
            <Sheet open={invoicesOpen} onOpenChange={setInvoicesOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="relative">
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Invoices</span>
                  {openInvoices.length > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {openInvoices.length}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md">
                <SheetHeader><SheetTitle>My Invoices</SheetTitle></SheetHeader>
                <Tabs value={invoiceTab} onValueChange={setInvoiceTab} className="mt-4">
                  <TabsList className="w-full">
                    <TabsTrigger value="open" className="flex-1">Open ({openInvoices.length})</TabsTrigger>
                    <TabsTrigger value="closed" className="flex-1">Closed</TabsTrigger>
                  </TabsList>
                  <TabsContent value="open" className="mt-4">
                    <ScrollArea className="h-[70vh]">
                      {openInvoices.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No open invoices</p>
                      ) : (
                        <div className="space-y-2">
                          {openInvoices.map((inv) => (
                            <Card key={inv.id} className={cn("p-3 cursor-pointer hover:bg-muted/50", inv.payment_status === "partially_paid" && "border-amber-300 dark:border-amber-700")} onClick={() => openInvoiceForEdit(inv)}>
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-mono text-sm font-medium">{inv.invoice_number?.slice(-8)}</p>
                                  <p className="text-sm">Table {inv.table_number || 'N/A'}</p>
                                  {inv.created_by_name && (
                                    <p className="text-[11px] text-muted-foreground leading-tight">By: {inv.created_by_name}</p>
                                  )}
                                  {inv.payment_status === "partially_paid" && (
                                    <p className="text-xs text-amber-600 font-medium mt-0.5" data-testid={`partial-badge-${inv.id}`}>
                                      Paid: {formatCurrency(inv.amount_paid || 0)} | Due: {formatCurrency(inv.balance_due || 0)}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right">
                                  {inv.payment_status === "partially_paid" ? (
                                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Partial</Badge>
                                  ) : getStatusBadge(inv.status)}
                                  <p className="font-mono font-bold text-primary mt-1">{formatCurrency(inv.total)}</p>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                  <TabsContent value="closed" className="mt-4">
                    <ScrollArea className="h-[70vh]">
                      {closedInvoices.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No closed invoices</p>
                      ) : (
                        <div className="space-y-2">
                          {closedInvoices.map((inv) => (
                            <Card key={inv.id} className="p-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-mono text-sm font-medium">{inv.invoice_number?.slice(-8)}</p>
                                  <p className="text-xs text-muted-foreground">{new Date(inv.completed_at || inv.created_at).toLocaleString()}</p>
                                  {inv.created_by_name && (
                                    <p className="text-[11px] text-muted-foreground leading-tight">By: {inv.created_by_name}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setViewingInvoice(inv);
                                      setViewInvoiceOpen(true);
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <div className="text-right">
                                    {getStatusBadge(inv.status)}
                                    <p className="font-mono font-bold mt-1">{formatCurrency(inv.total)}</p>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </SheetContent>
            </Sheet>
          )}

          {/* Order Type */}
          <Tabs value={orderType} onValueChange={setOrderType} className="hidden md:block">
            <TabsList>
              <TabsTrigger value="dine_in"><UtensilsCrossed className="h-4 w-4 mr-1" />Dine In</TabsTrigger>
              <TabsTrigger value="takeaway"><ShoppingBag className="h-4 w-4 mr-1" />Takeaway</TabsTrigger>
            </TabsList>
          </Tabs>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1 h-9">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline max-w-[80px] truncate">{user?.full_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/admin")}><Settings className="mr-2 h-4 w-4" />Admin</DropdownMenuItem>
              <DropdownMenuItem onClick={toggleTheme}>{theme === "dark" ? "Light" : "Dark"} Mode</DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-2">
                <IdleTimeoutSettingsInline />
              </div>
              {canCloseShift && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { fetchCurrentShift(); setShiftDialogOpen(true); }}>
                    <Square className="mr-2 h-4 w-4" />Close Shift
                  </DropdownMenuItem>
                  {currentShift && (
                    <DropdownMenuItem onClick={() => printShiftSummary(currentShift.id)} data-testid="print-shift-summary-btn">
                      <FileText className="mr-2 h-4 w-4" />Print Shift Summary
                    </DropdownMenuItem>
                  )}
                  {canRunEod && (
                    <DropdownMenuItem onClick={() => navigate("/admin/shifts")}>
                      <Moon className="mr-2 h-4 w-4" />End of Day
                    </DropdownMenuItem>
                  )}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive"><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Product Groups & Categories Filter */}
          <div className="border-b bg-background/95 backdrop-blur shrink-0">
            {/* Product Groups - Only show if there are multiple groups */}
            {activeGroups.length > 1 && (
              <div className="px-3 md:px-4 pt-3 md:pt-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Menu:</span>
                  <div className="flex gap-1.5 flex-wrap">
                    <button
                      onClick={() => handleGroupChange("all")}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                        selectedGroup === "all"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "bg-muted hover:bg-muted/80 text-muted-foreground"
                      }`}
                    >
                      All Items
                    </button>
                    {activeGroups.map((group) => (
                      <button
                        key={group.id}
                        onClick={() => handleGroupChange(group.id)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                          selectedGroup === group.id
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "bg-muted hover:bg-muted/80 text-muted-foreground"
                        }`}
                      >
                        {group.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Categories */}
            <div className="p-3 md:p-4">
              <ScrollArea className="w-full">
                <div className="flex gap-2 pb-2">
                  <Button 
                    variant={selectedCategory === "all" ? "default" : "outline"} 
                    size="sm" 
                    onClick={() => setSelectedCategory("all")} 
                    className="shrink-0 h-9"
                  >
                    All {selectedGroup !== "all" ? `${activeGroups.find(g => g.id === selectedGroup)?.name || ''}` : ''}
                  </Button>
                  {categoriesWithProducts.map((cat) => (
                    <Button 
                      key={cat.id} 
                      variant={selectedCategory === cat.id ? "default" : "outline"} 
                      size="sm" 
                      onClick={() => setSelectedCategory(cat.id)} 
                      className="shrink-0 h-9"
                    >
                      {cat.name}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Products */}
          <ScrollArea className="flex-1 p-3 md:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-3 pb-24 md:pb-4">
              {filteredProducts.map((product) => (
                <button key={product.id} onClick={() => handleProductClick(product)}
                  className="pos-item-card group p-2 md:p-3 touch-manipulation active:scale-95 transition-transform">
                  <div className="w-full h-20 md:h-24 rounded-lg overflow-hidden mb-2 bg-muted">
                    {product.image_url ? (
                      <img 
                        src={resolveImageUrl(product.image_url)} 
                        alt={product.name} 
                        className="w-full h-full object-cover" 
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} 
                      />
                    ) : null}
                    <div className={`w-full h-full items-center justify-center ${product.image_url ? 'hidden' : 'flex'}`}>
                      <Package className="h-8 w-8 text-muted-foreground" />
                    </div>
                  </div>
                  <span className="text-xs md:text-sm font-medium text-center line-clamp-2">{product.name}</span>
                  <span className="text-sm font-mono text-primary font-semibold mt-1">{formatCurrency(product.price)}</span>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Desktop Cart */}
        <div className="hidden md:flex w-80 lg:w-96 border-l bg-card flex-col">
          <CartContent />
        </div>
      </div>

      {/* Mobile FAB */}
      <div className="md:hidden fixed bottom-20 right-4 z-50">
        <Drawer open={cartDrawerOpen} onOpenChange={setCartDrawerOpen}>
          <DrawerTrigger asChild>
            <Button size="lg" className={cn("h-16 w-16 rounded-full shadow-2xl relative", isInvoiceMode && "bg-orange-500 hover:bg-orange-600")}>
              {isInvoiceMode ? <Send className="h-6 w-6" /> : <ShoppingCart className="h-6 w-6" />}
              {(cartItemCount > 0 || activeInvoice) && (
                <Badge className="absolute -top-1 -right-1 h-6 w-6 p-0 flex items-center justify-center bg-destructive">{activeInvoice ? "!" : cartItemCount}</Badge>
              )}
            </Button>
          </DrawerTrigger>
          <DrawerContent className="max-h-[85vh]">
            <DrawerHeader className="pb-0"><DrawerTitle className="sr-only">Cart</DrawerTitle></DrawerHeader>
            <div className="flex flex-col h-full"><CartContent inDrawer /></div>
          </DrawerContent>
        </Drawer>
      </div>

      {/* Checkout/Settlement Dialog - Extracted Component */}
      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        activeInvoice={activeInvoice}
        finalTotal={finalTotal}
        formatCurrency={formatCurrency}
        canApplyDiscount={canApplyDiscount}
        appliedDiscount={appliedDiscount}
        promoDiscount={promoDiscount}
        discountCode={discountCode}
        setDiscountCode={setDiscountCode}
        setAppliedDiscount={setAppliedDiscount}
        setPromoDiscount={setPromoDiscount}
        applyDiscountCode={applyDiscountCode}
        paymentMethod={paymentMethod}
        setPaymentMethod={setPaymentMethod}
        noCashPayments={noCashPayments}
        cardSettings={cardSettings}
        dpoSettings={dpoSettings}
        pesapalPosSettings={pesapalPosSettings}
        cardDetails={cardDetails}
        setCardDetails={setCardDetails}
        nfcPaymentStatus={nfcPaymentStatus}
        setNfcPaymentStatus={setNfcPaymentStatus}
        nfcPollingInterval={nfcPollingInterval}
        setNfcPaymentRequestId={setNfcPaymentRequestId}
        mpesaSettings={mpesaSettings}
        mpesaPhone={mpesaPhone}
        setMpesaPhone={setMpesaPhone}
        mpesaMethod={mpesaMethod}
        setMpesaMethod={setMpesaMethod}
        loadingC2BPayments={loadingC2BPayments}
        availableC2BPayments={availableC2BPayments}
        fetchAvailableC2BPayments={fetchAvailableC2BPayments}
        selectedC2BPayment={selectedC2BPayment}
        setSelectedC2BPayment={setSelectedC2BPayment}
        c2bMpesaCode={c2bMpesaCode}
        setC2bMpesaCode={setC2bMpesaCode}
        c2bVerifyResult={c2bVerifyResult}
        setC2bVerifyResult={setC2bVerifyResult}
        c2bVerifying={c2bVerifying}
        handleVerifyC2BPayment={handleVerifyC2BPayment}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        creditCheckResult={creditCheckResult}
        checkCreditAvailability={checkCreditAvailability}
        creditApprovalPending={creditApprovalPending}
        canCreditSale={canCreditSale}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        fetchCustomers={fetchCustomers}
        customersLoading={customersLoading}
        customersList={customersList}
        processing={processing}
        handleCloseInvoice={handleCloseInvoice}
        handleExpressCheckout={handleExpressCheckout}
        amountTendered={amountTendered}
        setAmountTendered={setAmountTendered}
      />

      {/* DPO Card Terminal (Push to Pay) status dialog */}
      <DpoTerminalDialog
        open={showDpoDialog}
        invoice={dpoInvoice}
        channel={dpoInvoice?.channel}
        formatCurrency={formatCurrency}
        onClose={() => {
          setShowDpoDialog(false);
          setDpoInvoice(null);
          setActiveInvoice(null);
          resetState();
          fetchInvoices();
        }}
        onPaid={async (receiptLines) => {
          setShowDpoDialog(false);
          // Print the card terminal slip (customer copy) if the terminal returned one
          if (Array.isArray(receiptLines) && receiptLines.length) {
            try {
              const paperSize = invoiceTemplate?.paper_size || "80mm";
              const bizName = invoiceTemplate?.business_name || currentOutlet?.name || "";
              openPrintWindow(generateCardSlipHTML(receiptLines, paperSize, bizName), "Card Receipt");
            } catch { /* ignore slip print errors */ }
          }
          try {
            if (dpoInvoice?.id) {
              const printResult = await api.post(`/invoices/${dpoInvoice.id}/print?destination=receipt`);
              if (printResult.print_data) await handleAutoPrint(printResult.print_data);
            }
          } catch { /* ignore print errors */ }
          toast.success("Card payment approved!");
          setDpoInvoice(null);
          setActiveInvoice(null);
          resetState();
          fetchInvoices();
        }}
      />
      
      {/* Pesapal Card Terminal (Push to Pay) status dialog */}
      <PesapalTerminalDialog
        open={showPesapalPosDialog}
        invoice={pesapalPosInvoice}
        channel={pesapalPosInvoice?.channel || "card"}
        formatCurrency={formatCurrency}
        onClose={() => {
          setShowPesapalPosDialog(false);
          setPesapalPosInvoice(null);
          setActiveInvoice(null);
          resetState();
          fetchInvoices();
        }}
        onPaid={async () => {
          setShowPesapalPosDialog(false);
          try {
            if (pesapalPosInvoice?.id) {
              const printResult = await api.post(`/invoices/${pesapalPosInvoice.id}/print?destination=receipt`);
              if (printResult.print_data) await handleAutoPrint(printResult.print_data);
            }
          } catch { /* ignore print errors */ }
          toast.success("Card payment approved!");
          setPesapalPosInvoice(null);
          setActiveInvoice(null);
          resetState();
          fetchInvoices();
        }}
      />
      
      {/* C2B Payment Instructions Dialog - Extracted Component */}
      <C2BInstructionsDialog
        open={c2bDialogOpen}
        onOpenChange={setC2bDialogOpen}
        mpesaSettings={mpesaSettings}
        invoice={c2bInvoice}
        formatCurrency={formatCurrency}
        processing={processing}
        onConfirm={handleC2BPayment}
      />

      {/* Void Dialog - Extracted Component */}
      <VoidItemDialog
        open={voidDialogOpen}
        onOpenChange={setVoidDialogOpen}
        item={voidItem}
        reason={voidReason}
        onReasonChange={setVoidReason}
        onVoid={handleVoidItem}
        onCancel={() => setVoidItem(null)}
      />

      {/* Shift Management Dialog - Extracted Component */}
      <ShiftDialog
        open={shiftDialogOpen}
        onOpenChange={setShiftDialogOpen}
        currentShift={currentShift}
        closingCash={closingCash}
        onClosingCashChange={setClosingCash}
        bankDeposit={bankDeposit}
        onBankDepositChange={setBankDeposit}
        floatRetained={floatRetained}
        onFloatRetainedChange={setFloatRetained}
        shiftNotes={shiftNotes}
        onShiftNotesChange={setShiftNotes}
        onCloseShift={handleCloseShift}
        shiftLoading={shiftLoading}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />

      {/* View Closed Invoice Dialog - Extracted Component */}
      <ViewInvoiceDialog
        open={viewInvoiceOpen}
        onOpenChange={setViewInvoiceOpen}
        invoice={viewingInvoice}
        formatCurrency={formatCurrency}
        getStatusBadge={getStatusBadge}
      />

      {/* Split Bill Dialog */}
      <SplitBillDialog
        open={splitBillOpen}
        onOpenChange={setSplitBillOpen}
        invoice={activeInvoice}
        onSplitComplete={(updatedInvoice) => {
          // Refresh the active invoice after merge
          if (updatedInvoice) {
            setActiveInvoice(updatedInvoice);
          }
          fetchInvoices();
        }}
        onPaySplit={async (split) => {
          // Set the split as active invoice and open payment dialog
          setActiveInvoice(split);
          setSplitBillOpen(false);
          await refreshMpesaSettings();
          setCheckoutOpen(true);
        }}
      />

      {/* Sync Status Panel */}
      {showSyncPanel && (
        <SyncStatusPanel 
          apiUrl={process.env.REACT_APP_BACKEND_URL}
          token={localStorage.getItem('auth_token')}
          onClose={() => setShowSyncPanel(false)}
        />
      )}

      {/* Printer Manager Dialog */}
      <PrinterManagerDialog 
        open={showPrinterManager} 
        onOpenChange={setShowPrinterManager} 
      />

      {/* Product Customization Modal */}
      <ProductCustomizationModal
        open={customizationModalOpen}
        onClose={() => {
          setCustomizationModalOpen(false);
          setProductToCustomize(null);
        }}
        product={productToCustomize}
        onAddToCart={handleAddCustomizedProduct}
      />

      {/* Customer Selection Sheet - Extracted Component */}
      <POSCustomerSheet
        open={customerSheetOpen}
        onOpenChange={setCustomerSheetOpen}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        fetchCustomers={fetchCustomers}
        customersLoading={customersLoading}
        customersList={customersList}
        selectedCustomer={selectedCustomer}
        onSelectCustomer={handleSelectCustomer}
        onClearCustomer={clearSelectedCustomer}
      />
    </div>
  );
}
